/**
 * Threaded internal notes for any record — property, apartment, room, tenant.
 *
 * One feed shape everywhere: a root note, its replies, an optional attachment,
 * an author and a resolved/open state. Backed by `public.admin_notes`, which is
 * admin-only under RLS (super_admin / tech / admin — never tenants or owners).
 *
 * Two rules are enforced by the database, not here, and the hooks below only
 * surface their errors:
 *   - a note belongs to exactly one record (CHECK admin_notes_scope_exactly_one)
 *   - only its author, or a super admin, may edit or delete it
 *     (trigger admin_notes_guard_authorship). Any admin may resolve any thread.
 *
 * @see supabase/migrations/20260812100000_admin_notes_entity_scope_threads.sql
 */
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { resolveStoredFileUrl } from "@/lib/storage-url";
import { isMissingColumn } from "./useContractFlowReady";
/**
 * The feed shows rows this panel wrote, and nothing else.
 *
 * `admin_notes` is a shared table. Besides notes it holds the contract card's
 * admin-only note and the audit row the key/contract upload flow records — and,
 * historically, whatever the older tenant notes box collected. Opening the
 * drawer onto that mixture showed three file uploads and a stray "test" where
 * an operator expected their notes.
 *
 * So the panel STAMPS what it writes (`kind = 'general'`) and reads back only
 * that. Two properties fall out of it, both wanted:
 *
 *   - it needs no migration and no date cutoff to be correct — an untagged row
 *     is, by definition, not something this panel wrote
 *   - a future surface that parks something in this table is excluded by
 *     default, instead of appearing in the notes list until somebody notices
 *
 * Nothing is lost: the older rows still render on the surfaces that own them
 * (the Documents tab, the Activity Timeline, the per-payment notes box), which
 * read through `useAdminNotes` and are untouched by this.
 */
export const WRITTEN_NOTE_KIND = "general";

function isWrittenNote(row: { kind: string | null }) {
  return row.kind === WRITTEN_NOTE_KIND;
}

/** Private bucket for note attachments — signed URLs only, never public. */
export const NOTE_BUCKET = "admin-notes";

// ─── Scope ──────────────────────────────────────────────────────────────────

/**
 * Which record a feed belongs to. Exactly one level may be set; `tenant` is the
 * one level spanning two columns, because a tenant note is addressed by booking
 * and/or user (matching the pre-existing tenant feed).
 */
export type NoteScope =
  | { level: "property"; propertyId: number }
  | { level: "apartment"; apartmentId: number }
  | { level: "room"; roomId: number }
  | { level: "owner"; lessorId: string }
  | { level: "tenant"; bookingId?: number | null; userId?: string | null };

export type NoteLevel = NoteScope["level"];

/** Stable, fully-discriminating cache key — two levels can share an id. */
export function noteScopeKey(scope: NoteScope | null): readonly unknown[] {
  if (!scope) return ["v2", "entity-notes", "none"] as const;
  switch (scope.level) {
    case "property":
      return ["v2", "entity-notes", "property", scope.propertyId] as const;
    case "apartment":
      return ["v2", "entity-notes", "apartment", scope.apartmentId] as const;
    case "room":
      return ["v2", "entity-notes", "room", scope.roomId] as const;
    case "owner":
      return ["v2", "entity-notes", "owner", scope.lessorId] as const;
    case "tenant":
      return [
        "v2",
        "entity-notes",
        "tenant",
        scope.bookingId ?? null,
        scope.userId ?? null,
      ] as const;
  }
}

/** Columns written on INSERT so the row lands in this scope's feed. */
function scopeInsertColumns(scope: NoteScope): Record<string, unknown> {
  switch (scope.level) {
    case "property":
      return { property_id: scope.propertyId };
    case "apartment":
      return { apartment_id: scope.apartmentId };
    case "room":
      return { room_id: scope.roomId };
    case "owner":
      return { lessor_id: scope.lessorId };
    case "tenant":
      return {
        booking_id: scope.bookingId ?? null,
        user_id: scope.userId ?? null,
      };
  }
}

/** A scope with nothing to point at yields no feed rather than every row. */
function scopeIsAddressable(scope: NoteScope): boolean {
  if (scope.level === "tenant")
    return scope.bookingId != null || !!scope.userId;
  // A lessor id is a uuid string: an empty one would filter on nothing.
  if (scope.level === "owner") return !!scope.lessorId;
  return true;
}

/** Storage key prefix, so an attachment is filed under the record it describes. */
function scopeStoragePrefix(scope: NoteScope): string {
  switch (scope.level) {
    case "property":
      return `property/${scope.propertyId}`;
    case "apartment":
      return `apartment/${scope.apartmentId}`;
    case "room":
      return `room/${scope.roomId}`;
    case "owner":
      return `owner/${scope.lessorId}`;
    case "tenant":
      return scope.bookingId != null
        ? `tenant/booking-${scope.bookingId}`
        : `tenant/user-${scope.userId}`;
  }
}

// ─── Rows ───────────────────────────────────────────────────────────────────

export type NotePerson = {
  id: string;
  name: string | null;
  email: string | null;
};

export type NoteRow = {
  id: string;
  parent_id: string | null;
  body: string | null;
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  mime_type: string | null;
  kind: string | null;
  created_by: string | null;
  created_at: string;
  edited_at: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  /** Set once the note has been retracted; such rows never reach the feed. */
  deleted_at: string | null;
  author: NotePerson | null;
  resolver: NotePerson | null;
  /** Bookmarked by the CURRENT admin — per-person, never shared. */
  saved: boolean;
};

export type NoteThread = NoteRow & { replies: NoteRow[] };

const THREAD_COLS = [
  "id",
  "parent_id",
  "body",
  "file_url",
  "file_name",
  "file_size",
  "mime_type",
  "kind",
  "created_by",
  "created_at",
  "edited_at",
  "resolved_at",
  "resolved_by",
  "deleted_at",
  "property_id",
  "apartment_id",
  "room_id",
  "booking_id",
  "user_id",
  "payment_id",
].join(", ");

/**
 * Legacy column list — what `admin_notes` looks like before
 * 20260812100000. Used only so the TENANT feed keeps rendering on a database
 * that is a migration behind; the new levels have nowhere to read from and say
 * so instead of failing red.
 */
const LEGACY_COLS =
  "id, body, file_url, file_name, file_size, mime_type, kind, created_by, created_at, booking_id, user_id, payment_id";

// ─── Schema-drift probe ─────────────────────────────────────────────────────
// Dev and LIVE drift (see 20260805… / rent_paid_through). Selecting a column a
// database does not have fails the WHOLE query with PostgREST 42703, which would
// blank the feed rather than degrade it. Ask once, remember the answer, and only
// let the NEGATIVE one expire — it stops being true the moment `supabase db
// push` runs, and the page should notice without a hard reload.

let threadSupport: { present: boolean; at: number } | null = null;
const SUPPORT_TTL_MS = 60_000;

function knownThreadSupport(): boolean | null {
  if (!threadSupport) return null;
  if (threadSupport.present) return true;
  return Date.now() - threadSupport.at < SUPPORT_TTL_MS ? false : null;
}

/** Reset by tests, and after a schema change is applied in-session. */
export function resetEntityNotesSchemaSupport(): void {
  threadSupport = null;
}

// ─── Read ───────────────────────────────────────────────────────────────────

type RawNote = Record<string, unknown>;

function toRow(raw: RawNote): NoteRow {
  return {
    id: String(raw.id),
    parent_id: (raw.parent_id as string | null) ?? null,
    body: (raw.body as string | null) ?? null,
    file_url: (raw.file_url as string | null) ?? null,
    file_name: (raw.file_name as string | null) ?? null,
    file_size: (raw.file_size as number | null) ?? null,
    mime_type: (raw.mime_type as string | null) ?? null,
    kind: (raw.kind as string | null) ?? null,
    created_by: (raw.created_by as string | null) ?? null,
    created_at: String(raw.created_at),
    edited_at: (raw.edited_at as string | null) ?? null,
    resolved_at: (raw.resolved_at as string | null) ?? null,
    resolved_by: (raw.resolved_by as string | null) ?? null,
    deleted_at: (raw.deleted_at as string | null) ?? null,
    author: null,
    resolver: null,
    saved: false,
  };
}

/**
 * Which of these notes the CURRENT admin has saved.
 *
 * The whole table is selected and RLS narrows it to the caller's own rows —
 * deliberately, rather than filtering on a user id the client supplies, which
 * would be a value the client could change.
 *
 * A database without `admin_note_saves` yet answers "none saved" instead of
 * taking the feed down with it.
 */
async function fetchSavedIds(): Promise<Set<string>> {
  const { data, error } = await supabase
    .from("admin_note_saves")
    .select("note_id");
  if (error) return new Set();
  return new Set(
    ((data ?? []) as Array<{ note_id: string }>).map((r) => r.note_id)
  );
}

/**
 * Hydrate author + resolver from `profiles`. `created_by` references
 * auth.users, which PostgREST cannot join to, so it is a second round trip.
 */
async function hydratePeople(rows: NoteRow[]): Promise<NoteRow[]> {
  const ids = Array.from(
    new Set(rows.flatMap((r) => [r.created_by, r.resolved_by]).filter(Boolean))
  ) as string[];
  if (!ids.length) return rows;

  const { data } = await supabase
    .from("profiles")
    .select("id, name, email")
    .in("id", ids);
  const byId = new Map<string, NotePerson>(
    ((data ?? []) as NotePerson[]).map((p) => [p.id, p])
  );
  for (const r of rows) {
    r.author = r.created_by ? (byId.get(r.created_by) ?? null) : null;
    r.resolver = r.resolved_by ? (byId.get(r.resolved_by) ?? null) : null;
  }
  return rows;
}

/**
 * Roots newest-first — the freshest context is what an operator opening a record
 * wants — but replies oldest-first inside each thread, because a conversation
 * only reads correctly forwards.
 */
function assembleThreads(rows: NoteRow[]): NoteThread[] {
  const roots: NoteThread[] = [];
  const byRoot = new Map<string, NoteRow[]>();

  for (const r of rows) {
    if (r.parent_id) {
      const list = byRoot.get(r.parent_id);
      if (list) list.push(r);
      else byRoot.set(r.parent_id, [r]);
    }
  }
  for (const r of rows) {
    if (!r.parent_id) {
      const replies = (byRoot.get(r.id) ?? []).sort((a, b) =>
        a.created_at.localeCompare(b.created_at)
      );
      roots.push({ ...r, replies });
    }
  }
  return roots.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export type NotesFeed = {
  threads: NoteThread[];
  /**
   * False when this database predates the threads migration. The tenant feed
   * still returns flat notes; the other levels return nothing, and the UI shows
   * a "run the migration" state rather than an error.
   */
  schemaReady: boolean;
};

function applyScopeFilter(query: any, scope: NoteScope): any {
  let q = query;
  switch (scope.level) {
    case "property":
      return q.eq("property_id", scope.propertyId);
    case "apartment":
      return q.eq("apartment_id", scope.apartmentId);
    case "room":
      return q.eq("room_id", scope.roomId);
    case "owner":
      return q.eq("lessor_id", scope.lessorId);
    case "tenant": {
      // Payment-scoped rows have their own surface on the payment row; showing
      // them here too would print the same note twice in one profile.
      q = q.is("payment_id", null);
      const ors: string[] = [];
      if (scope.bookingId != null) ors.push(`booking_id.eq.${scope.bookingId}`);
      if (scope.userId) ors.push(`user_id.eq.${scope.userId}`);
      return ors.length ? q.or(ors.join(",")) : q;
    }
  }
}

/**
 * One feed read. Exported so the scope filtering, the thread assembly and the
 * schema-drift fallback are testable without standing up a React tree — they
 * are the parts that decide whether an operator sees the right notes.
 */
export async function fetchNotesFeed(scope: NoteScope): Promise<NotesFeed> {
  const runLegacy = async (): Promise<NotesFeed> => {
    // The new levels live entirely in columns this database lacks.
    if (scope.level !== "tenant") return { threads: [], schemaReady: false };
    const res = await applyScopeFilter(
      supabase.from("admin_notes").select(LEGACY_COLS),
      scope
    );
    if (res.error) throw res.error;
    const rows = ((res.data ?? []) as RawNote[]).map(toRow);
    return {
      threads: assembleThreads(await hydratePeople(rows.filter(isWrittenNote))),
      schemaReady: false,
    };
  };

  if (knownThreadSupport() === false) return runLegacy();

  const res = await applyScopeFilter(
    supabase.from("admin_notes").select(THREAD_COLS),
    scope
  );

  if (res.error) {
    // Only a missing column justifies degrading. Treating a transient failure
    // as "the environment is behind" would report an empty feed for a record
    // that has notes — a confident wrong answer, worse than a visible error.
    if (!isMissingColumn(res.error)) throw res.error;
    // The owner feed filters on `lessor_id`, which only exists from
    // 20260816130000. A database in between the two migrations answers 42703
    // for the OWNER scope while property/apartment/room are perfectly fine, so
    // this failure must not be remembered as "no thread support" — that would
    // blank those other feeds for a minute because one owner modal was opened.
    if (scope.level === "owner") return { threads: [], schemaReady: false };
    threadSupport = { present: false, at: Date.now() };
    return runLegacy();
  }

  threadSupport = { present: true, at: Date.now() };
  const [saved, rows] = await Promise.all([
    fetchSavedIds(),
    Promise.resolve(((res.data ?? []) as RawNote[]).map(toRow)),
  ]);
  const live = rows.filter(
    // A retracted note keeps its row (the reason has to survive) but leaves the
    // feed. Its replies fall away with it: assembleThreads only keeps a reply
    // whose root is still present.
    (r) => !r.deleted_at && isWrittenNote(r)
  );
  for (const r of live) r.saved = saved.has(r.id);
  return {
    threads: assembleThreads(await hydratePeople(live)),
    schemaReady: true,
  };
}

const EMPTY_FEED: NotesFeed = { threads: [], schemaReady: true };

/**
 * Every thread on a record, newest root first.
 *
 * `null` is accepted so a page can ask for the count before its id has
 * resolved — hooks cannot be called conditionally, and a fake id would fetch
 * somebody else's feed.
 */
export function useEntityNotes(scope: NoteScope | null) {
  const enabled = !!scope && scopeIsAddressable(scope);
  const query = useQuery({
    queryKey: noteScopeKey(scope),
    enabled,
    queryFn: () => fetchNotesFeed(scope as NoteScope),
  });
  return { ...query, data: query.data ?? EMPTY_FEED };
}

/**
 * Unresolved thread count for a tab badge. Reads the same cache entry as
 * `useEntityNotes`, so a page showing both spends one request, not two.
 */
export function useOpenNoteCount(scope: NoteScope | null): number {
  const { data } = useEntityNotes(scope);
  return useMemo(
    () => data.threads.filter((t) => !t.resolved_at).length,
    [data.threads]
  );
}

/**
 * Every note in a feed as one flat list, roots and replies alike, newest first.
 *
 * The panel renders threads because a conversation reads that way; a history
 * does not — it wants each thing that happened, in the order it happened.
 */
export function flattenNotes(threads: NoteThread[]): NoteRow[] {
  const rows: NoteRow[] = [];
  for (const t of threads) {
    const { replies, ...root } = t;
    rows.push(root);
    rows.push(...replies);
  }
  return rows.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

/** A file attached to a note, lifted out of its thread. */
export type NoteAttachmentRow = {
  /** The note the file hangs off — its id, so a list has stable keys. */
  noteId: string;
  file_url: string;
  file_name: string | null;
  file_size: number | null;
  created_at: string;
  /** The note's text, when it had any — the caption for the file. */
  body: string | null;
  author: NotePerson | null;
};

/**
 * Every attachment in a feed, newest first, roots and replies alike.
 *
 * A file dropped on a note is a document about that record, but it lived only
 * inside the notes panel — invisible to anyone reading the Documents tab or the
 * activity feed, which is where people look for "what do we have on file". This
 * lifts them out without moving them: the note stays the owner of the file.
 */
export function noteAttachments(threads: NoteThread[]): NoteAttachmentRow[] {
  const rows: NoteAttachmentRow[] = [];
  const take = (n: NoteRow) => {
    if (!n.file_url) return;
    rows.push({
      noteId: n.id,
      file_url: n.file_url,
      file_name: n.file_name,
      file_size: n.file_size,
      created_at: n.created_at,
      body: n.body,
      author: n.author,
    });
  };
  for (const t of threads) {
    take(t);
    for (const r of t.replies) take(r);
  }
  return rows.sort((a, b) => b.created_at.localeCompare(a.created_at));
}

// ─── Attachments ────────────────────────────────────────────────────────────

export type NoteFile = {
  path: string;
  name: string;
  size: number;
  mimeType: string | null;
};

/**
 * `file_url` holds a storage PATH for anything written since the private
 * bucket landed, and an absolute URL for the tenant notes written before it.
 * Both must keep opening.
 */
export function isLegacyPublicUrl(fileUrl: string): boolean {
  return /^https?:\/\//i.test(fileUrl);
}

/** 25 MB — an operator note is a scan or a photo, not a video. */
export const NOTE_FILE_MAX_BYTES = 25 * 1024 * 1024;

export async function uploadNoteAttachment(
  scope: NoteScope,
  file: File
): Promise<NoteFile> {
  if (file.size > NOTE_FILE_MAX_BYTES) {
    throw new Error(
      `“${file.name}” is ${formatBytes(file.size)}. Attachments are limited to ${formatBytes(NOTE_FILE_MAX_BYTES)}.`
    );
  }
  const safeName = file.name.replace(/[^\w.-]+/g, "_");
  const path = `${scopeStoragePrefix(scope)}/${Date.now()}-${safeName}`;
  const { error } = await supabase.storage
    .from(NOTE_BUCKET)
    .upload(path, file, {
      contentType: file.type || "application/octet-stream",
      upsert: false,
    });
  if (error) throw error;
  return {
    path,
    name: file.name,
    size: file.size,
    mimeType: file.type || null,
  };
}

/**
 * A link to an attachment, valid for five minutes. The bucket is private, so
 * there is no permanent URL to store — `getPublicUrl` would hand back one that
 * 400s.
 */
export async function noteAttachmentHref(
  row: {
    file_url: string | null;
    file_name?: string | null;
  },
  opts?: {
    /**
     * Ask storage for a link that downloads instead of rendering. It sets
     * Content-Disposition server-side, which is the only thing that works here:
     * an `<a download>` is ignored cross-origin, and the signed URL always is.
     */
    download?: boolean;
  }
): Promise<string | null> {
  if (!row.file_url) return null;
  // A legacy absolute URL is the public tenant-documents URL an older note
  // stored. That bucket is going private (COL-436), so sign the path it names.
  if (isLegacyPublicUrl(row.file_url)) {
    return resolveStoredFileUrl(row.file_url, NOTE_BUCKET, {
      expiresIn: 300,
      download: opts?.download ? row.file_name || true : undefined,
    });
  }
  const { data, error } = await supabase.storage
    .from(NOTE_BUCKET)
    .createSignedUrl(
      row.file_url,
      300,
      opts?.download ? { download: row.file_name || true } : undefined
    );
  if (error) throw error;
  return data?.signedUrl ?? null;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Write ──────────────────────────────────────────────────────────────────

/**
 * An UPDATE or DELETE the database refused can come back as zero rows and no
 * error. Reporting that as success is the worst outcome available: the admin
 * then trusts an edit that never happened. Every mutation below asks for the
 * affected ids and checks them.
 */
function assertAffected(rows: unknown, what: string): void {
  if (Array.isArray(rows) && rows.length > 0) return;
  throw new Error(
    `${what} Either the database refused the change, or another admin removed the note first — reopen the record to see where it stands.`
  );
}

/**
 * Refresh every surface a note write is visible on — not just this feed.
 *
 * `admin_notes` has a second reader: `useAdminNotes`, which backs the Activity
 * Timeline, the Documents tab and the per-payment note boxes under a DIFFERENT
 * cache key. Invalidating only this drawer's key meant a note (or its
 * attachment) landed here instantly and appeared nowhere else until the page
 * was reloaded — which reads as "the file was not saved", the one conclusion
 * that is wrong.
 *
 * The second key is invalidated by prefix, so it covers the tenant feed and
 * every payment-scoped one without knowing which of them is mounted.
 */
function useInvalidateScope(scope: NoteScope) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: noteScopeKey(scope) });
    void qc.invalidateQueries({ queryKey: ["v2", "admin-notes"] });
  };
}

export function useAddNote(scope: NoteScope) {
  const invalidate = useInvalidateScope(scope);
  return useMutation({
    mutationFn: async (input: {
      body: string | null;
      createdBy: string;
      /** Set to answer an existing thread. The database copies the scope over. */
      parentId?: string | null;
      file?: NoteFile | null;
    }) => {
      const body = input.body?.trim() || null;
      if (!body && !input.file) {
        throw new Error("Write something, or attach a file.");
      }
      const { error } = await supabase.from("admin_notes").insert({
        ...scopeInsertColumns(scope),
        // The stamp the feed reads back on. Without it the note is written but
        // never shown — see WRITTEN_NOTE_KIND.
        kind: WRITTEN_NOTE_KIND,
        parent_id: input.parentId ?? null,
        body,
        created_by: input.createdBy,
        file_url: input.file?.path ?? null,
        file_name: input.file?.name ?? null,
        file_size: input.file?.size ?? null,
        mime_type: input.file?.mimeType ?? null,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function useEditNote(scope: NoteScope) {
  const invalidate = useInvalidateScope(scope);
  return useMutation({
    mutationFn: async (input: { id: string; body: string }) => {
      const body = input.body.trim();
      if (!body)
        throw new Error("A note cannot be emptied — delete it instead.");
      const { data, error } = await supabase
        .from("admin_notes")
        .update({ body })
        .eq("id", input.id)
        .select("id");
      if (error) throw error;
      assertAffected(data, "The note was not saved.");
    },
    onSuccess: invalidate,
  });
}

export function useResolveNote(scope: NoteScope) {
  const invalidate = useInvalidateScope(scope);
  return useMutation({
    mutationFn: async (input: {
      id: string;
      resolved: boolean;
      actorId: string | null;
    }) => {
      const { data, error } = await supabase
        .from("admin_notes")
        .update(
          input.resolved
            ? {
                resolved_at: new Date().toISOString(),
                resolved_by: input.actorId ?? null,
              }
            : { resolved_at: null, resolved_by: null }
        )
        .eq("id", input.id)
        .select("id");
      if (error) throw error;
      assertAffected(
        data,
        input.resolved
          ? "The note was not resolved."
          : "The note was not reopened."
      );
    },
    onSuccess: invalidate,
  });
}

/**
 * Retract a note, with the reason on the record.
 *
 * A soft delete, not a DELETE: the UI asks why, and a reason that vanishes with
 * the row answers nobody's question three months later. The feed filters
 * retracted notes out, so it reads as a delete to everyone using it.
 */
export function useDeleteNote(scope: NoteScope) {
  const invalidate = useInvalidateScope(scope);
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) => {
      const reason = input.reason.trim();
      if (!reason) throw new Error("Give a reason for deleting this note.");
      const { data, error } = await supabase
        .from("admin_notes")
        .update({
          deleted_at: new Date().toISOString(),
          deleted_reason: reason,
        })
        .eq("id", input.id)
        .select("id");
      if (error) throw error;
      assertAffected(data, "The note was not deleted.");
    },
    onSuccess: invalidate,
  });
}

/**
 * Rename a note's attachment.
 *
 * `file_name` is the label only — the stored object keeps its key, so nothing
 * that already points at the file breaks. The Documents tab offers this because
 * an attachment arrives named whatever the uploader's machine called it
 * ("Bildschirmfoto 2026-07-13 um 13.13.06.png"), which says nothing about what
 * it is three months later.
 *
 * `admin_notes_guard_authorship` allows this for the note's author and super
 * admins only, and RAISES for anyone else — so the database's own message is
 * what the caller shows, rather than a silent no-op.
 */
export function useRenameNoteAttachment(scope: NoteScope) {
  const invalidate = useInvalidateScope(scope);
  return useMutation({
    mutationFn: async (input: { id: string; fileName: string }) => {
      const fileName = input.fileName.trim();
      if (!fileName) throw new Error("Give the attachment a name.");
      const { data, error } = await supabase
        .from("admin_notes")
        .update({ file_name: fileName })
        .eq("id", input.id)
        .select("id");
      if (error) throw error;
      assertAffected(data, "The attachment was not renamed.");
    },
    onSuccess: invalidate,
  });
}

/**
 * Bookmark a note for yourself. Per-admin: your saved list is invisible to
 * everyone else, which is why it is a row in `admin_note_saves` rather than a
 * flag on the note.
 */
export function useToggleNoteSave(scope: NoteScope) {
  const invalidate = useInvalidateScope(scope);
  return useMutation({
    mutationFn: async (input: {
      id: string;
      saved: boolean;
      userId: string;
    }) => {
      if (input.saved) {
        const { error } = await supabase
          .from("admin_note_saves")
          .insert({ note_id: input.id, user_id: input.userId });
        // Saving something already saved is not a failure worth reporting.
        if (error && error.code !== "23505") throw error;
        return;
      }
      const { error } = await supabase
        .from("admin_note_saves")
        .delete()
        .eq("note_id", input.id)
        .eq("user_id", input.userId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}
